// ============================================================
//  GEOGRAPHY PAGE — ADMIN DASHBOARD
//  Path: src/pages/GeographyPage.tsx
//  Geographic drill-down: Country → Province → City → Detail
// ============================================================

import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { getTaskerBreakdown, getGeographyData } from '../services/admin.service'

// ── Province abbreviation → full name ────────────────────────
const PROVINCE_NAMES: Record<string, string> = {
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba',
  NB: 'New Brunswick', NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia', NT: 'Northwest Territories', NU: 'Nunavut',
  ON: 'Ontario', PE: 'Prince Edward Island', QC: 'Quebec',
  SK: 'Saskatchewan', YT: 'Yukon',
}
const expandProvince = (p: string | null) =>
  p ? (PROVINCE_NAMES[p.toUpperCase()] ?? p) : 'Unknown Province'

// ── Types ─────────────────────────────────────────────────────
// Every level now carries the same four figures the city view shows
// (backend: admin.service.js's getTaskerBreakdown). `total` / `count`
// are the original expert-count fields, kept by the backend so older
// pages keep working; the new fields are optional so this page still
// renders against a backend that hasn't been redeployed yet.
interface PlaceCounts {
  experts?: number
  onlineExperts?: number
  customers?: number | null
  activeCustomers?: number | null
}
interface CityRow     extends PlaceCounts { city: string | null; count: number }
interface ProvinceRow extends PlaceCounts { province: string | null; total: number; cities: CityRow[] }
interface CountryRow  extends PlaceCounts { country: string | null; total: number; provinces: ProvinceRow[] }
interface BreakdownResult {
  totalExperts: number
  totalOnlineExperts?: number
  totalCustomers?: number | null
  totalActiveCustomers?: number | null
  breakdown: CountryRow[]
}

// Sent for a blank country/province/city so the backend returns ONLY
// people with no address there. Previously null was sent, axios dropped
// it from the query string, and "Unknown City" listed the whole province.
const GEO_UNKNOWN = '__unknown__'

// "12 experts · 30 customers" line used on country, province and city rows.
const CountsLine: React.FC<{ experts: number; customers?: number | null; online?: number }> = ({ experts, customers, online }) => (
  <div className="flex items-center gap-4 flex-wrap">
    <div>
      <p className="text-xl font-bold text-purple-400">{experts}</p>
      <p className="text-xs text-gray-500">expert{experts !== 1 ? 's' : ''}{online != null ? ` · ${online} online` : ''}</p>
    </div>
    {customers != null && (
      <div>
        <p className="text-xl font-bold text-blue-400">{customers}</p>
        <p className="text-xs text-gray-500">customer{customers !== 1 ? 's' : ''}</p>
      </div>
    )}
  </div>
)

interface ExpertRow {
  id: string; userId: string
  firstName: string | null; lastName: string | null
  email: string | null; phone: string | null
  status: string; isOnline: boolean; lastSeenAt: string | null
  city: string | null; province: string | null; country: string | null
  categories: string[]
}
interface CustomerRow {
  id: string; userId: string
  firstName: string | null; lastName: string | null
  email: string | null; phone: string | null
  joinedAt: string | null; taskCount: number
}
interface GeoDetail {
  experts: ExpertRow[]; expertTotal: number
  customers: CustomerRow[]; customerTotal: number
  // Exact place-wide figures (new backend). Absent on an older backend,
  // in which case the page falls back to counting the current page.
  onlineExpertTotal?: number
  activeCustomerTotal?: number
  page: number; pageSize: number
}

// ── Helpers ───────────────────────────────────────────────────
const STATUS_COLORS: Record<string, string> = {
  approved:         'bg-green-500/20 text-green-400',
  pending:          'bg-yellow-500/20 text-yellow-400',
  under_review:     'bg-blue-500/20 text-blue-400',
  rejected:         'bg-red-500/20 text-red-400',
  suspended:        'bg-gray-500/20 text-gray-400',
  background_check: 'bg-purple-500/20 text-purple-400',
}

const relTime = (iso: string | null): string => {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000), h = Math.floor(diff / 3600000), d = Math.floor(diff / 86400000)
  if (m < 5)  return 'Just now'
  if (h < 1)  return `${m}m ago`
  if (h < 24) return `${h}h ago`
  return `${d}d ago`
}

const Avatar = ({ name, color }: { name: string | null; color: string }) => (
  <div className={`w-9 h-9 rounded-full ${color} flex items-center justify-center text-white text-sm font-bold flex-shrink-0`}>
    {name?.[0]?.toUpperCase() ?? '?'}
  </div>
)

type Level = 'countries' | 'provinces' | 'cities' | 'detail'

// ── Component ─────────────────────────────────────────────────
const GeographyPage: React.FC = () => {
  const navigate = useNavigate()
  const [tree, setTree]           = useState<BreakdownResult | null>(null)
  const [treeLoading, setTreeLoading] = useState(true)
  const [treeError, setTreeError] = useState('')
  const [level, setLevel]         = useState<Level>('countries')
  const [selCountry, setSelCountry]   = useState<string | null>(null)
  const [selProvince, setSelProvince] = useState<string | null>(null)
  const [selCity, setSelCity]         = useState<string | null>(null)
  const [detail, setDetail]       = useState<GeoDetail | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)
  // FIX: a failed city-detail load used to be swallowed (catch-ignore),
  // leaving the tabs with nothing under them and no message.
  const [detailError, setDetailError] = useState('')
  const [detailReloadKey, setDetailReloadKey] = useState(0)
  const [treeReloadKey, setTreeReloadKey] = useState(0)
  const [tab, setTab]             = useState<'experts' | 'customers'>('experts')
  const [page, setPage]           = useState(1)

  const errorText = (err: any, what: string) =>
    err?.response?.data?.message || (err?.isNetworkError
      ? 'Could not reach the server — check your connection and try again.'
      : `Failed to load ${what}`)

  useEffect(() => {
    setTreeLoading(true)
    setTreeError('')
    getTaskerBreakdown()
      .then((res: any) => setTree(res?.data?.data ?? null))
      .catch((err: any) => {
        console.error('Failed to load geography overview:', err)
        setTreeError(errorText(err, 'data'))
      })
      .finally(() => setTreeLoading(false))
  }, [treeReloadKey])

  const loadDetail = useCallback(async (country: string | null, province: string | null, city: string | null, p: number) => {
    setDetailLoading(true)
    setDetailError('')
    try {
      const res: any = await getGeographyData({
        country:  country  ?? GEO_UNKNOWN,
        province: province ?? GEO_UNKNOWN,
        city:     city     ?? GEO_UNKNOWN,
        page: p, limit: 20,
      })
      setDetail(res?.data?.data ?? null)
    } catch (err: any) {
      console.error('Failed to load city detail:', err)
      setDetail(null)
      setDetailError(errorText(err, 'this city'))
    }
    finally { setDetailLoading(false) }
  }, [])

  useEffect(() => {
    if (level === 'detail') loadDetail(selCountry, selProvince, selCity, page)
  }, [level, selCountry, selProvince, selCity, page, loadDetail, detailReloadKey])

  const goBack = () => {
    if (level === 'detail')    { setLevel('cities');    setDetail(null) }
    else if (level === 'cities')    setLevel('provinces')
    else if (level === 'provinces') setLevel('countries')
  }

  const countryRow = tree?.breakdown.find(c => (c.country ?? null) === selCountry)
  const provinceRow = countryRow?.provinces.find(p => (p.province ?? null) === selProvince)
  const allExperts = tree?.totalExperts ?? tree?.breakdown.reduce((s, c) => s + c.total, 0) ?? 0
  // Subtitle under a country/province heading: "12 experts · 30 customers"
  const peopleSummary = (row?: PlaceCounts & { total: number }) => {
    const experts = row?.experts ?? row?.total ?? 0
    const parts = [`${experts} expert${experts !== 1 ? 's' : ''}`]
    if (row?.customers != null) parts.push(`${row.customers} customer${row.customers !== 1 ? 's' : ''}`)
    return parts.join(' · ')
  }

  // ── Breadcrumb ──────────────────────────────────────────────
  const Breadcrumb = () => (
    <nav className="flex items-center gap-1.5 text-xs text-gray-500 mb-5 flex-wrap">
      <button onClick={() => setLevel('countries')} className="hover:text-gray-300 transition-colors">All Countries</button>
      {level !== 'countries' && (<><span>›</span><button onClick={() => setLevel('provinces')} className="hover:text-gray-300 transition-colors">{selCountry ?? 'Unknown'}</button></>)}
      {(level === 'cities' || level === 'detail') && (<><span>›</span><button onClick={() => setLevel('cities')} className="hover:text-gray-300 transition-colors">{expandProvince(selProvince)}</button></>)}
      {level === 'detail' && (<><span>›</span><span className="text-gray-300">{selCity ?? 'Unknown'}</span></>)}
    </nav>
  )

  if (treeLoading) return <div className="flex items-center justify-center h-64 text-gray-500">Loading...</div>
  if (treeError)   return (
    <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 flex items-center justify-between gap-3">
      <p className="text-red-400">{treeError}</p>
      <button onClick={() => setTreeReloadKey((k) => k + 1)}
        className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0">
        Try again
      </button>
    </div>
  )

  // ── Level 1: Countries ──────────────────────────────────────
  if (level === 'countries') return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white">Geographic Overview</h1>
        <p className="text-gray-400 text-sm mt-1">Drill down from country → province → city to see who's registered where</p>
      </div>

      {/* Global summary — the same four people figures the city view
          shows, platform-wide, then the place counts. The people cards
          only appear once the backend sends them (tree.totalCustomers). */}
      {tree?.totalCustomers != null && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
          {[
            { label: 'Total Experts',    value: allExperts,                        icon: '🔧', color: 'bg-purple-500/20 text-purple-400' },
            { label: 'Online Now',       value: tree.totalOnlineExperts ?? '—',    icon: '🟢', color: 'bg-green-500/20 text-green-400' },
            { label: 'Total Customers',  value: tree.totalCustomers,               icon: '🛒', color: 'bg-blue-500/20 text-blue-400' },
            { label: 'Active Customers', value: tree.totalActiveCustomers ?? '—',  icon: '✅', color: 'bg-teal-500/20 text-teal-400' },
          ].map(card => (
            <div key={card.label} className="bg-surface-card border border-border-light rounded-2xl p-4">
              <div className={`w-9 h-9 rounded-xl ${card.color} flex items-center justify-center mb-3 text-base`}>{card.icon}</div>
              <p className="text-xl font-bold text-white">{card.value}</p>
              <p className="text-xs text-gray-400 mt-0.5">{card.label}</p>
            </div>
          ))}
        </div>
      )}
      <div className={`grid gap-3 mb-8 ${tree?.totalCustomers != null ? 'grid-cols-3' : 'grid-cols-2 sm:grid-cols-4'}`}>
        {[
          ...(tree?.totalCustomers != null ? [] : [{ label: 'Total Experts', value: allExperts, icon: '🔧', color: 'bg-purple-500/20 text-purple-400' }]),
          { label: 'Countries',     value: tree?.breakdown.length ?? 0, icon: '🌍', color: 'bg-green-500/20 text-green-400' },
          { label: 'Provinces',     value: tree?.breakdown.flatMap(c => c.provinces).length ?? 0, icon: '📍', color: 'bg-blue-500/20 text-blue-400' },
          { label: 'Cities',        value: tree?.breakdown.flatMap(c => c.provinces).flatMap(p => p.cities).length ?? 0, icon: '🏙️', color: 'bg-orange-500/20 text-orange-400' },
        ].map(card => (
          <div key={card.label} className="bg-surface-card border border-border-light rounded-2xl p-4">
            <div className={`w-9 h-9 rounded-xl ${card.color} flex items-center justify-center mb-3 text-base`}>{card.icon}</div>
            <p className="text-xl font-bold text-white">{card.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{card.label}</p>
          </div>
        ))}
      </div>

      {/* Country cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(tree?.breakdown ?? []).map(row => (
          <button key={row.country ?? '__null__'}
            onClick={() => { setSelCountry(row.country); setSelProvince(null); setSelCity(null); setLevel('provinces') }}
            className="bg-surface-card border border-border-light rounded-2xl p-6 text-left hover:border-brand-500/50 hover:bg-surface-tertiary/30 transition-all group"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-2xl bg-green-500/15 flex items-center justify-center text-2xl">🌍</div>
              <span className="text-xs text-gray-500 group-hover:text-brand-400 transition-colors mt-1">{row.provinces.length} province{row.provinces.length !== 1 ? 's' : ''} →</span>
            </div>
            <h2 className="text-lg font-bold text-white mb-3">{row.country ?? 'Unknown'}</h2>
            <CountsLine experts={row.experts ?? row.total} customers={row.customers} online={row.onlineExperts} />
          </button>
        ))}
        {(tree?.breakdown ?? []).length === 0 && (
          <div className="col-span-3 bg-surface-card border border-border-light rounded-2xl p-12 text-center text-gray-500">
            No experts or customers with address data yet.
          </div>
        )}
      </div>
    </div>
  )

  // ── Level 2: Provinces ──────────────────────────────────────
  if (level === 'provinces') return (
    <div>
      <button onClick={goBack} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-white mb-5 transition-colors">← Back</button>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">{selCountry ?? 'Unknown'}</h1>
        <p className="text-gray-400 text-sm mt-1">{peopleSummary(countryRow)} across {countryRow?.provinces.length ?? 0} provinces</p>
      </div>
      <Breadcrumb />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {(countryRow?.provinces ?? []).map(p => (
          <button key={p.province ?? '__null__'}
            onClick={() => { setSelProvince(p.province); setSelCity(null); setLevel('cities') }}
            className="bg-surface-card border border-border-light rounded-2xl p-5 text-left hover:border-brand-500/50 hover:bg-surface-tertiary/30 transition-all group"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center text-lg">📍</div>
              <span className="text-xs text-gray-500 group-hover:text-brand-400 transition-colors mt-1">{p.cities.length} cit{p.cities.length !== 1 ? 'ies' : 'y'} →</span>
            </div>
            <h3 className="text-base font-semibold text-white mb-2">{expandProvince(p.province)}</h3>
            {p.province && p.province !== expandProvince(p.province) && (
              <p className="text-xs text-gray-600 mb-2">{p.province}</p>
            )}
            <CountsLine experts={p.experts ?? p.total} customers={p.customers} online={p.onlineExperts} />
          </button>
        ))}
      </div>
    </div>
  )

  // ── Level 3: Cities ─────────────────────────────────────────
  if (level === 'cities') return (
    <div>
      <button onClick={goBack} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-white mb-5 transition-colors">← Back</button>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">{expandProvince(selProvince)}</h1>
        <p className="text-gray-400 text-sm mt-1">{peopleSummary(provinceRow)} across {provinceRow?.cities.length ?? 0} cities</p>
      </div>
      <Breadcrumb />
      {/* Cities — compact list, works well for 2 or 30 */}
      <div className="bg-surface-card border border-border-light rounded-2xl overflow-hidden">
        {(provinceRow?.cities ?? []).length === 0 ? (
          <div className="p-12 text-center text-gray-500">No cities found.</div>
        ) : (
          <div className="divide-y divide-border-light">
            {(provinceRow?.cities ?? []).map((c: CityRow) => (
              <button key={c.city ?? '__null__'}
                onClick={() => { setSelCity(c.city); setPage(1); setTab('experts'); setDetail(null); setLevel('detail') }}
                className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-surface-tertiary/30 transition-colors text-left group"
              >
                <span className="text-base">🏙️</span>
                <span className="flex-1 text-sm font-medium text-gray-200 group-hover:text-white transition-colors">
                  {c.city ?? 'Unknown City'}
                </span>
                {/* Red = customers here but no experts yet (demand with no supply). */}
                <span className={`px-2.5 py-1 text-xs rounded-full font-medium ${
                  c.count === 0 ? 'bg-red-500/20 text-red-400' : 'bg-purple-500/20 text-purple-400'
                }`}>
                  {c.count} expert{c.count !== 1 ? 's' : ''}{c.onlineExperts ? ` · ${c.onlineExperts} online` : ''}
                </span>
                {c.customers != null && (
                  <span className="px-2.5 py-1 text-xs rounded-full font-medium bg-blue-500/20 text-blue-400">
                    {c.customers} customer{c.customers !== 1 ? 's' : ''}
                  </span>
                )}
                <span className="text-gray-600 group-hover:text-gray-400 transition-colors text-xs">›</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )

  // ── Level 4: City detail ─────────────────────────────────────
  return (
    <div>
      <button onClick={goBack} className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-white mb-5 transition-colors">← Back</button>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">{selCity ?? 'Unknown'}</h1>
        <p className="text-gray-400 text-sm mt-1">{expandProvince(selProvince)}, {selCountry ?? 'Unknown'}</p>
      </div>
      <Breadcrumb />

      {/* Summary.
          FIX: "Online Now" and "Active Customers" are counted from the
          rows on the CURRENT page only (the backend returns totals for
          experts and customers, not for online/active). They were
          labelled as city-wide figures, so any city with more than one
          page under-reported and the numbers changed page to page. They
          keep the plain label only when every row fits on this page
          (then the count IS city-wide); otherwise they say "this page". */}
      {detail && (() => {
        // Exact city-wide totals from the backend when available; the
        // page-count fallback (labelled "this page") only applies to an
        // older backend that doesn't send them.
        const exactOnline = detail.onlineExpertTotal
        const exactActive = detail.activeCustomerTotal
        const allExpertsShown   = exactOnline != null || detail.expertTotal   <= detail.experts.length
        const allCustomersShown = exactActive != null || detail.customerTotal <= detail.customers.length
        return (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total Experts',    value: detail.expertTotal,   color: 'text-purple-400', bg: 'bg-purple-500/15', icon: '🔧' },
            { label: allExpertsShown ? 'Online Now' : 'Online Now (this page)', value: exactOnline ?? detail.experts.filter(e => e.isOnline).length, color: 'text-green-400', bg: 'bg-green-500/15', icon: '🟢' },
            { label: 'Total Customers',  value: detail.customerTotal, color: 'text-blue-400',   bg: 'bg-blue-500/15',   icon: '🛒' },
            { label: allCustomersShown ? 'Active Customers' : 'Active Customers (this page)', value: exactActive ?? detail.customers.filter(c => c.taskCount > 0).length, color: 'text-teal-400', bg: 'bg-teal-500/15', icon: '✅' },
          ].map(s => (
            <div key={s.label} className="bg-surface-card border border-border-light rounded-2xl p-4">
              <div className={`w-9 h-9 rounded-xl ${s.bg} flex items-center justify-center mb-3 text-sm`}>{s.icon}</div>
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
        )
      })()}

      {/* Tabs */}
      <div className="flex gap-2 mb-5">
        {(['experts', 'customers'] as const).map(t => (
          <button key={t} onClick={() => { setTab(t); setPage(1) }}
            className={`px-5 py-2 text-sm font-medium rounded-xl transition-colors ${tab === t ? 'bg-brand-600 text-white' : 'bg-surface-card border border-border-light text-gray-400 hover:text-white'}`}>
            {t === 'experts' ? `🔧 Experts${detail ? ` (${detail.expertTotal})` : ''}` : `🛒 Customers${detail ? ` (${detail.customerTotal})` : ''}`}
          </button>
        ))}
      </div>

      {detailLoading ? (
        <div className="bg-surface-card border border-border-light rounded-2xl p-12 text-center text-gray-500">Loading...</div>
      ) : detailError ? (
        <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 flex items-center justify-between gap-3">
          <p className="text-red-400 text-sm">{detailError}</p>
          <button onClick={() => setDetailReloadKey((k) => k + 1)}
            className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg hover:bg-border-light transition-colors shrink-0">
            Try again
          </button>
        </div>
      ) : !detail ? null : tab === 'experts' ? (
        <div className="bg-surface-card border border-border-light rounded-2xl overflow-hidden">
          {detail.experts.length === 0 ? (
            <div className="p-12 text-center text-gray-500">No experts in this city yet.</div>
          ) : (
            <div className="divide-y divide-border-light">
              {detail.experts.map(e => (
                <div key={e.id} className="flex items-center gap-4 px-5 py-4 hover:bg-surface-tertiary/30 transition-colors">
                  <Avatar name={e.firstName} color="bg-brand-700" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-white">{e.firstName} {e.lastName}</p>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${STATUS_COLORS[e.status] ?? 'bg-gray-500/20 text-gray-400'}`}>{e.status.replace(/_/g,' ')}</span>
                      {e.isOnline ? <span className="px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-400">🟢 Online</span> : <span className="px-2 py-0.5 text-xs rounded-full bg-surface-tertiary text-gray-500">⚫ Offline</span>}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{e.email || '—'}</p>
                    {e.categories.length > 0 && <p className="text-xs text-gray-600 mt-0.5 truncate">{e.categories.slice(0,3).join(', ')}{e.categories.length > 3 ? ` +${e.categories.length - 3}` : ''}</p>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs text-gray-400">{relTime(e.lastSeenAt)}</p>
                    <button onClick={() => navigate(`/experts/${e.id}`)} className="mt-1.5 px-3 py-1 text-xs bg-brand-500/15 text-brand-400 rounded-lg hover:bg-brand-500/25 transition-colors">View</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {detail.expertTotal > detail.pageSize && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-border-light">
              <p className="text-xs text-gray-500">Page {page} of {Math.ceil(detail.expertTotal / detail.pageSize)}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light">Prev</button>
                <button onClick={() => setPage(p => p+1)} disabled={page >= Math.ceil(detail.expertTotal / detail.pageSize)} className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light">Next</button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-surface-card border border-border-light rounded-2xl overflow-hidden">
          {detail.customers.length === 0 ? (
            <div className="p-12 text-center text-gray-500">No customers in this city yet.</div>
          ) : (
            <div className="divide-y divide-border-light">
              {detail.customers.map(c => (
                <div key={c.id} className="flex items-center gap-4 px-5 py-4 hover:bg-surface-tertiary/30 transition-colors">
                  <Avatar name={c.firstName} color="bg-green-700" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white">{c.firstName} {c.lastName}</p>
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{c.email || '—'}</p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span className={`px-2.5 py-1 text-xs rounded-full ${c.taskCount > 0 ? 'bg-teal-500/20 text-teal-400' : 'bg-surface-tertiary text-gray-500'}`}>
                      {c.taskCount} task{c.taskCount !== 1 ? 's' : ''}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">{c.joinedAt ? new Date(c.joinedAt).toLocaleDateString() : '—'}</p>
                    <button onClick={() => navigate(`/customers/${c.userId}`)} className="mt-1 px-3 py-1 text-xs bg-green-500/15 text-green-400 rounded-lg hover:bg-green-500/25 transition-colors">View</button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {detail.customerTotal > detail.pageSize && (
            <div className="flex items-center justify-between px-5 py-3 border-t border-border-light">
              <p className="text-xs text-gray-500">Page {page} of {Math.ceil(detail.customerTotal / detail.pageSize)}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1} className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light">Prev</button>
                <button onClick={() => setPage(p => p+1)} disabled={page >= Math.ceil(detail.customerTotal / detail.pageSize)} className="px-3 py-1.5 text-xs bg-surface-tertiary text-gray-300 rounded-lg disabled:opacity-30 hover:bg-border-light">Next</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default GeographyPage